import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
} from '@material-ui/core';

import SaveButton from 'app/components/SaveButton';
import SearchIcon from '@material-ui/icons/Search';

import helpers from 'app/utils/helpers';
import notification from 'app/config/notification';

import userEndpoints from 'app/api/services/userEndpoints';

import CustomPaginationActionsTable from 'app/components/TableList/TableList';
import {
  clearAlertMessage,
  clearCity,
  clearDeletedVehicles,
  clearFiles,
  clearInternalNewRequest,
  clearVehicles,
} from 'app/store/slices/forms';
import { clearServiceDetails, clearServiceDocuments } from 'app/store/slices/services';
import { clearNewRequest, clearRequestDetails } from 'app/store/slices/requests';
import { clearActualRevision, clearRevisions } from 'app/store/slices/revisions';
import { clearInspectedVehicles, clearVehiclePlateByCompany } from 'app/store/slices/vehicle';
import companyEndpoints from '../../../../api/services/companyEndpoints';
import { USER_SECTORS } from '../../../../utils/constants';

const UserData = props => {
  const { classes, request } = props;  
  const name = request.deRazaoSocial || request.nmCompleto || request.nmUsuario || '';
  const document = helpers.formatarDocumento(request.deCnpjMatriz || request.deCpf || request.deCpfCnpj || '');
  const address = '';

  return (
    <Grid container spacing={0} className={classes.requestResult}>
      <Grid item xs={12}>
        <b>{name}</b> <br />
        {document} <br />
        {address}
      </Grid>
    </Grid>
  );
};

const UserSelection = props => {
  const dispatch = useDispatch();
  const { classes, intl, handleSubmit, handleCancel } = props;

  const user = useSelector(state => state.auth.user);
  const isDplo = useMemo(() => {
    return !user.setores ? null : user.setores.find((e) => USER_SECTORS.DQS_DPLO === e.cdOrgaoSetor);
  },[user]);

  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [filters, setFilters] = useState({});
  const [cdSelected, setCdSelected] = useState(null);

  const handleChange = event => {
    setCdSelected(event.target.value);
  };

  const handleSearch = async () => {
    setPage(0);
    const temp = { ...filters };
    temp['txtBusca'] = search;
    setFilters(temp);
  };

  const onChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = event => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const init = () => {
      dispatch(clearAlertMessage());
      dispatch(clearCity());
      dispatch(clearFiles());
      dispatch(clearServiceDetails());
      dispatch(clearServiceDocuments());
      dispatch(clearVehicles());
      dispatch(clearNewRequest());
      dispatch(clearRequestDetails());
      dispatch(clearRevisions());
      dispatch(clearActualRevision());
      dispatch(clearVehiclePlateByCompany());
      dispatch(clearInspectedVehicles());
      dispatch(clearDeletedVehicles());
      dispatch(clearInternalNewRequest());
    };
    init();
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    const findRequests = async () => {
      const search = {
        page: page,
        size: pageSize,
        request: { ...filters },
      };

      let response = {};
      if (!isDplo) {
        response = await userEndpoints.getUsersPagination(search);
      } else {
        response = await companyEndpoints.getCompanies(search);
      }

      if (response?.status === 200 && response.data) {
        const entriesTemp = [];
        const rowsTemp = [];
        let index = 0;
        for (const r of response.data.content) {
          const temp = { ...r };

          if(temp.cdEmpresaPadrao){
            let responseCompany = await companyEndpoints.getCompany({ cdEmpresa: temp.cdEmpresaPadrao });
            if (responseCompany.status === 200 && responseCompany.data) {
              temp.deRazaoSocial = responseCompany.data.deRazaoSocial;
              temp.deCnpjMatriz = responseCompany.data.deCnpjMatriz;
            }
          }

          entriesTemp.push(temp);
          const rowsPrepared = await prepareData(temp, index);
          rowsTemp.push(rowsPrepared);
          index++;
        }
        setPagination(response.data);
        setEntries(entriesTemp);
        setRows(rowsTemp);
      }
      setLoading(false);
    };
    findRequests();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, pageSize]);

  const columns = useMemo(() => {
    let fields = [
      {
        title: '',
        field: 'cdSelected',
        align: 'left',
      },
    ];
    return fields;
  }, []);

  const prepareData = async (content, i) => {
    return {
      originalData: content,
      cdSelected: (
        <>
          <FormControlLabel
            key={`req-${i}`}
            className={classes.radio}
            value={String(content.cdUsuario || content.cdEmpresa)}
            control={<Radio />}
            label={<UserData request={content} classes={classes} />}
          />
        </>
      ),
    };
  };

  return (
    <Grid container spacing={3} className={classes.containerForm}>
      <Grid item xs={12}>
        <FormControl fullWidth variant='outlined'>
          <InputLabel htmlFor='outlined-adornment-search'>
            Digite o CNPJ/CPF ou Nome da empresa/pessoa
          </InputLabel>
          <OutlinedInput
            id='outlined-adornment-search'
            value={search}
            onChange={e => setSearch(e.target.value)}
            endAdornment={
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={e => handleSearch()}
                  onMouseDown={() => {
                  }}
                  edge='end'
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
            labelWidth={400}
            onKeyPress={(ev) => {
              if (ev.key === 'Enter') {
                ev.preventDefault();
                handleSearch();
              }
            }}
          />
        </FormControl>
      </Grid>

      {loading && (
        <Grid item xs={12} className={classes.centerProgress}>
          <CircularProgress size={18} />
        </Grid>
      )}

      {!loading && (
        <Grid item xs={12}>
          {rows && rows.length > 0 && (
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <FormControl fullWidth component='fieldset'>
                  <RadioGroup
                    aria-label='gender'
                    name='gender1'
                    value={cdSelected}
                    onChange={handleChange}
                  >
                    <CustomPaginationActionsTable
                      loading={loading}
                      columns={columns}
                      rows={rows}
                      hideFilterOrder={true}
                      hideItemOrder={true}
                      hideHeader={true}
                      placeholderSearch='Busca'
                      pagination={pagination}
                      onChangePage={onChangePage}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                      onClickOrder={() => {
                      }}
                      onClickRow={() => {
                      }}
                      onChangeInitialDate={() => {
                      }}
                      onChangeFinalDate={() => {
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {(!entries || entries.length === 0) && (
            <Grid item xs={12} className={classes.noResults}>
              Nenhuma empresa encontrada
            </Grid>
          )}
        </Grid>
      )}
      <SaveButton
        mainColor={'primary'}
        hideActionBtn={false}
        isInternalForm={true}
        canEdit={true}
        onCancel={() => {
          handleCancel('cdUser');
        }}
        onSave={() => {
          if (entries) {
            if (cdSelected) {
              let list = [];
              let type = 'cdUsuario';
              if(isDplo) {
                type = 'company';
                list = entries.filter(c => Number(c.cdEmpresa) === Number(cdSelected));
              }else{
                list = entries.filter(c => Number(c.cdUsuario) === Number(cdSelected));
              }
              handleSubmit(type, cdSelected, list[0]);
            } else {
              notification(
                intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.NO_COMPANY_USER_SELECTED' }),
                { toastId: 'NO_COMPANY_USER_SELECTED', type: 'warning' },
              );
            }
          }
        }}
        cancelLabel={'Cancelar'}
        saveLabel={'Confirmar'}
      />
    </Grid>
  );
};

export default UserSelection;
